import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/Login";
import Layout from "@/pages/Layout";
import Dashboard from "@/pages/Dashboard";
import ChildrenPage from "@/pages/ChildrenPage";
import StaffPage from "@/pages/StaffPage";
import AttendancePage from "@/pages/AttendancePage";
import MessagesPage from "@/pages/MessagesPage";
import DailyReportsPage from "@/pages/DailyReportsPage";
import IEPPage from "@/pages/IEPPage";
import SessionsPage from "@/pages/SessionsPage";
import WhatsAppLogPage from "@/pages/WhatsAppLogPage";
import AuditLogPage from "@/pages/AuditLogPage";
import EmergencyPage from "@/pages/EmergencyPage";
import MonthlyReportsPage from "@/pages/MonthlyReportsPage";
import WhatsAppTemplatesPage from "@/pages/WhatsAppTemplatesPage";
import EvaluationsPage from "@/pages/EvaluationsPage";
import InvoicesPage from "@/pages/InvoicesPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import LandingPage from "@/pages/LandingPage";
import ContributionsPage from "@/pages/ContributionsPage";
import ArticlesAdminPage from "@/pages/ArticlesAdminPage";
import LandingSettingsPage from "@/pages/LandingSettingsPage";
import VideosPage from "@/pages/VideosPage";
import PlaylistsPage from "@/pages/PlaylistsPage";
import RoomsPage from "@/pages/RoomsPage";

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-slate-500 text-sm">جاري التحميل...</div>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user === null) return <LoadingScreen />;
  if (!user) return <Navigate to={pathname === "/" ? "/welcome" : "/login"} replace />;
  return children;
}

function GuestOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <LoadingScreen />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function RoleGate({ allow, children }) {
  const { user } = useAuth();
  if (!user) return null;
  if (!allow.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/welcome" element={<LandingPage />} />
      <Route path="/welcome/contributions" element={<ContributionsPage />} />
      <Route path="/login" element={<GuestOnly><LoginPage /></GuestOnly>} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="/children" element={<ChildrenPage />} />
        <Route path="/staff" element={<RoleGate allow={["admin"]}><StaffPage /></RoleGate>} />
        <Route path="/rooms" element={<RoleGate allow={["admin"]}><RoomsPage /></RoleGate>} />
        <Route path="/attendance" element={<RoleGate allow={["admin", "teacher"]}><AttendancePage /></RoleGate>} />
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/daily-reports" element={<DailyReportsPage />} />
        <Route path="/iep" element={<IEPPage />} />
        <Route path="/sessions" element={<SessionsPage />} />
        <Route path="/emergency" element={<EmergencyPage />} />
        <Route path="/monthly" element={<MonthlyReportsPage />} />
        <Route path="/evaluations" element={<EvaluationsPage />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/articles-admin" element={<RoleGate allow={["admin"]}><ArticlesAdminPage /></RoleGate>} />
        <Route path="/landing-settings" element={<RoleGate allow={["admin"]}><LandingSettingsPage /></RoleGate>} />
        <Route path="/videos" element={<VideosPage />} />
        <Route path="/playlists" element={<PlaylistsPage />} />
        <Route path="/playlists/:id" element={<PlaylistsPage />} />
        <Route path="/whatsapp" element={<RoleGate allow={["admin"]}><WhatsAppLogPage /></RoleGate>} />
        <Route path="/whatsapp-templates" element={<RoleGate allow={["admin"]}><WhatsAppTemplatesPage /></RoleGate>} />
        <Route path="/audit" element={<RoleGate allow={["admin"]}><AuditLogPage /></RoleGate>} />
      </Route>
      <Route path="*" element={<Navigate to="/welcome" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
